import React, { useEffect, useRef } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useData } from '../contexts/DataContext';

/**
 * Wrapper component that enables pull-to-refresh functionality
 * Only works when user is at the top of the page
 */
const PullToRefreshWrapper = ({ children }) => {
  const { refreshModules } = useData();
  const contentRef = useRef(null);

  const handleRefresh = async () => {
    // Refresh modules
    if (refreshModules) {
      refreshModules();
    }

    // Dispatch event to refresh classrooms
    window.dispatchEvent(new Event('refreshClassrooms'));

    // Dispatch event to refresh AppBanner usage
    window.dispatchEvent(new Event('refreshUsage'));

    // Small delay to allow UI to update
    return new Promise(resolve => setTimeout(resolve, 500));
  };

  const { isRefreshing, pullProgress, pullDistance } = usePullToRefresh(handleRefresh, {
    threshold: 80,
    enabled: true,
  });

  // Apply pull-down transform to the content (not the banner)
  useEffect(() => {
    const maxPull = 100; // Maximum pull distance for visual feedback
    const pullAmount = Math.min(pullDistance * 0.5, maxPull); // Scale down for smoother effect
    
    // Capture the current ref value for cleanup
    const contentElement = contentRef.current;
    
    if (contentElement) {
      if (pullAmount > 0 || isRefreshing) {
        // Apply transform to content wrapper only (not the banner)
        contentElement.style.transform = `translateY(${pullAmount}px)`;
        contentElement.style.transition = isRefreshing ? 'transform 0.3s ease-out' : 'none';
      } else {
        // Reset transform
        contentElement.style.transform = '';
        contentElement.style.transition = 'transform 0.3s ease-out';
      }
    }

    return () => {
      // Cleanup on unmount - use captured value
      if (contentElement) {
        contentElement.style.transform = '';
        contentElement.style.transition = '';
      }
    };
  }, [pullDistance, isRefreshing]);

  // Show spinner when pulling or refreshing
  const showSpinner = pullProgress > 0 || isRefreshing;
  const bannerHeight = 56; // App banner height in pixels

  // Calculate stroke-dasharray for hollow circle fill
  const circumference = 2 * Math.PI * 18; // radius is 18 (40px - 4px border = 36px diameter / 2 = 18px radius)
  const strokeDashoffset = circumference - (pullProgress * circumference);

  return (
    <>
      {showSpinner && (
        <div
          style={{
            position: 'fixed',
            top: bannerHeight + 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <svg
            width="40"
            height="40"
            style={{
              transform: 'rotate(-90deg)', // Start from top
            }}
          >
            {/* Background circle (empty perimeter) */}
            <circle
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke="#e0e0e0"
              strokeWidth="3"
            />
            {/* Filling circle (clockwise fill) */}
            <circle
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke="#386641"
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{
                transition: isRefreshing ? 'none' : 'stroke-dashoffset 0.1s ease-out',
              }}
            />
            {/* Spinning indicator when refreshing */}
            {isRefreshing && (
              <circle
                cx="20"
                cy="20"
                r="18"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                strokeDasharray="8 20"
                style={{
                  animation: 'spin 0.8s linear infinite',
                  transformOrigin: '20px 20px',
                }}
              />
            )}
          </svg>
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div ref={contentRef}>
        {children}
      </div>
    </>
  );
};

export default PullToRefreshWrapper;

